import { useEffect, useState } from 'react';

import { Button, Checkbox, Group } from '@mantine/core';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { scudMappingApi } from '@/shared/api/scudMapping';
import { SCUD_REASON_NAME_MAX_LENGTH } from '@/shared/constant/scudMapping';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction';
import { IScudMappingReason } from '@/shared/types/scudMapping.types';
import { LabelInput } from '@/shared/uikit/components/input';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';

import { SCUD_REASONS_KEY } from './queryKeys';
import { requestErrorText } from './utils';

type ScudReasonFormProps = {
	opened: boolean;
	/** null — новая причина */
	reason: IScudMappingReason | null;
	/** Порядок для новой причины — в конец списка */
	nextSortOrder: number;
	onClose: () => void;
};

/** Причина «Сопоставление не требуется» (ТЗ 4.2). Удаления нет: ненужную причину снимают с использования */
export const ScudReasonForm = ({ opened, reason, nextSortOrder, onClose }: ScudReasonFormProps) => {
	const queryClient = useQueryClient();
	const [name, setName] = useState('');
	const [sortOrder, setSortOrder] = useState<number>(nextSortOrder);
	const [commentRequired, setCommentRequired] = useState(false);
	const [isActive, setIsActive] = useState(true);

	useEffect(() => {
		if (!opened) {
			return;
		}

		setName(reason?.name ?? '');
		setSortOrder(reason?.sort_order ?? nextSortOrder);
		setCommentRequired(reason?.comment_required ?? false);
		setIsActive(reason?.is_active ?? true);
	}, [opened, reason?.id]);

	const trimmedName = name.trim();
	const nameError = !trimmedName ? 'Введите название' : null;

	const handleSaved = (text: string) => {
		// SCUD_REASONS_KEY — префикс и для полного списка, и для активных
		void queryClient.invalidateQueries({ queryKey: SCUD_REASONS_KEY });
		useShowNotifaction({ text, type: 'success' });
		onClose();
	};

	const handleError = (err: unknown) =>
		useShowNotifaction({
			text: requestErrorText(err, 'Не удалось сохранить причину'),
			type: 'error',
		});

	const { mutate: create, isPending: isCreating } = useMutation({
		mutationFn: scudMappingApi.createReason,
		onSuccess: () => handleSaved('Причина добавлена'),
		onError: handleError,
	});

	const { mutate: update, isPending: isUpdating } = useMutation({
		mutationFn: scudMappingApi.updateReason,
		onSuccess: () => handleSaved('Причина изменена'),
		onError: handleError,
	});

	const handleSubmit = () => {
		if (nameError) {
			return;
		}

		const payload = {
			name: trimmedName,
			sort_order: sortOrder,
			comment_required: commentRequired,
			is_active: isActive,
		};

		if (reason) {
			update({ id: reason.id, payload });
		} else {
			create(payload);
		}
	};

	return (
		<ModalSheet
			title={reason ? 'Изменение причины' : 'Новая причина'}
			opened={opened}
			onClose={onClose}
			size="32rem"
		>
			<LabelInput
				compact
				label="Название"
				maxLength={SCUD_REASON_NAME_MAX_LENGTH}
				value={name}
				onChange={(event) => setName(event.currentTarget.value)}
				clearable
				onClear={() => setName('')}
				error={name && nameError}
				data-autofocus
			/>
			<LabelInput
				compact
				label="Порядок в списке"
				inputMode="numeric"
				value={`${sortOrder}`}
				onChange={(event) => setSortOrder(Number(event.currentTarget.value.replace(/\D/g, '')))}
			/>
			<Checkbox
				label="Комментарий обязателен"
				checked={commentRequired}
				onChange={(event) => setCommentRequired(event.currentTarget.checked)}
			/>
			<Checkbox
				label="Используется"
				description="Снятая с использования причина остаётся в старых записях, но не предлагается в новых"
				checked={isActive}
				onChange={(event) => setIsActive(event.currentTarget.checked)}
			/>
			<Group justify="flex-end" gap="sm">
				<Button variant="outline" onClick={onClose}>
					Отмена
				</Button>
				<Button
					disabled={!!nameError}
					loading={isCreating || isUpdating}
					onClick={handleSubmit}
					title={nameError ?? undefined}
				>
					Сохранить
				</Button>
			</Group>
		</ModalSheet>
	);
};
